import { useMemo, useState } from "react";
import "./our-why.css";

const DEFAULT_OUR_WHY_YOUTUBE_URL = "https://www.youtube.com/watch?v=CS0SYj1bWk0";
const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

const FOUNDING_MESSAGE_PARAGRAPHS = [
  "People have never been more technologically connected yet felt more disconnected from the world around them. We have seldom been more divided as a nation, having traded real (and sometimes difficult) conversations in the endless pursuit of friends and followers on social media. Deep, productive dialogue has been replaced with character-limited posts on X, or chat rooms that are more likely to serve as echo chambers than a place for productive dialogue.",
  "The old adage “don’t discuss politics or religion at a dinner party” has been extended to daily life, and respectful disagreement has been replaced with cyber-bullying and harassment or simply canceling, blocking, and unfriending. Simply put, despite its numerous conveniences, the internet has contributed as much to isolating and dividing us as it has in truly connecting us.",
  "So, we’re offering the members of our community a platform to get back to basics by facilitating the most important social connections: those formed and nurtured between two willing participants. And although this website may be used to connect with people of common interests, we have designed it (and encourage you to use it) to identify and connect with someone you may otherwise have never had the opportunity to meet, including, most importantly, people that you may not agree with on one or more political or social issues.",
  "We welcome you to join us to help build our community and heal our world – one connection at a time. But come with an open mind. Come with an open heart, and - most importantly - come with Better Intentions.",
];

export function resolveOurWhyVideoId(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) return "";
  if (YOUTUBE_ID_PATTERN.test(normalizedValue)) return normalizedValue;

  try {
    const parsedUrl = new URL(normalizedValue);
    const host = parsedUrl.hostname.replace(/^www\./, "").toLowerCase();
    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);

    if (host === "youtu.be") {
      const directId = pathSegments[0] || "";
      return YOUTUBE_ID_PATTERN.test(directId) ? directId : "";
    }

    if (host.endsWith("youtube.com")) {
      const vParam = parsedUrl.searchParams.get("v") || "";
      if (YOUTUBE_ID_PATTERN.test(vParam)) return vParam;

      const embeddedId = pathSegments[1] || "";
      if (
        (pathSegments[0] === "embed" || pathSegments[0] === "shorts" || pathSegments[0] === "live") &&
        YOUTUBE_ID_PATTERN.test(embeddedId)
      ) {
        return embeddedId;
      }
    }
  } catch {
    return "";
  }

  return "";
}

export function getConfiguredOurWhyVideoId() {
  const ourWhyVideoUrl =
    process.env.REACT_APP_OUR_WHY_YOUTUBE_URL ||
    process.env.REACT_APP_PRELAUNCH_YOUTUBE_URL ||
    DEFAULT_OUR_WHY_YOUTUBE_URL;
  const ourWhyVideoId = process.env.REACT_APP_OUR_WHY_YOUTUBE_ID || "";

  return resolveOurWhyVideoId(ourWhyVideoUrl) || resolveOurWhyVideoId(ourWhyVideoId);
}

function FoundingMessageFallback() {
  return (
    <div className="ourWhy__fallback" role="status" aria-live="polite">
      <p className="ourWhy__fallbackEyebrow">Founding message</p>
      <div className="ourWhy__fallbackCopy">
        {FOUNDING_MESSAGE_PARAGRAPHS.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </div>
  );
}

export default function OurWhy() {
  const [hasVideoError, setHasVideoError] = useState(false);
  const videoId = useMemo(() => getConfiguredOurWhyVideoId(), []);
  const showVideo = Boolean(videoId) && !hasVideoError;

  return (
    <main className="ourWhy">
      <section className="ourWhy__content">
        <div className="ourWhy__inner">
          {showVideo ? (
            <div className="ourWhy__videoWrap">
              <iframe
                className="ourWhy__videoFrame"
                src={`https://www.youtube.com/embed/${videoId}`}
                title="Better Intentions founding video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                onLoad={() => setHasVideoError(false)}
                onError={() => setHasVideoError(true)}
              />
            </div>
          ) : (
            <div className="ourWhy__fallbackWrap">
              <FoundingMessageFallback />
            </div>
          )}

          <div className="ourWhy__story">
            <h1 className="ourWhy__title">Our Why</h1>
            <div className="ourWhy__copy">
              <p>
                People have never been more{" "}
                <span className="ourWhy__emphasis">technologically connected yet felt more disconnected</span>{" "}
                from the world around them. We have seldom been more divided as a nation, having traded
                real (and sometimes difficult) conversations in the endless pursuit of friends and
                followers on social media. Deep, productive dialogue has been replaced with
                character-limited posts on X, or chat rooms that are more likely to serve as echo
                chambers than a place for productive dialogue.
              </p>
              <p>
                The old adage “don’t discuss politics or religion at a dinner party” has been extended
                to daily life, and respectful disagreement has been replaced with cyber-bullying and
                harassment or simply canceling, blocking, and unfriending. Simply put, despite its
                numerous conveniences, the{" "}
                <span className="ourWhy__emphasis">
                  internet has contributed as much to isolating and dividing us as it has in truly
                  connecting us.
                </span>
              </p>
              <p>
                So, we’re offering the members of our{" "}
                <span className="ourWhy__emphasis">community a platform</span> to get back to basics by{" "}
                <span className="ourWhy__emphasis">
                  facilitating the most important social connections
                </span>
                : those formed and nurtured between two willing participants. And although this
                website may be used to connect with people of common interests, we have designed it
                (and encourage you to use it) to identify and connect with someone you may otherwise
                have never had the opportunity to meet, including, most importantly, people that you
                may not agree with on one or more political or social issues.
              </p>
              <p className="ourWhy__closing">
                We welcome you to join us to help build our community and heal our world – one
                connection at a time. But come with an open mind. Come with an open heart, and - most
                importantly - come with{" "}
                <span className="ourWhy__emphasis">Better Intentions</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="ourWhy__footer">
        <div className="ourWhy__footerInner">
          <a className="ourWhy__footerLink" href="/terms-of-service">Terms of Service</a>
          <span className="ourWhy__footerSeparator" aria-hidden="true">•</span>
          <a className="ourWhy__footerLink" href="/privacy-policy">Privacy Policy</a>
          <span className="ourWhy__footerSeparator" aria-hidden="true">•</span>
          <a
            className="ourWhy__socialLink"
            href="https://www.facebook.com/BetterIntentions"
            target="_blank"
            rel="noreferrer"
            aria-label="Better Intentions on Facebook"
            title="Facebook"
          >
            <svg className="ourWhy__socialIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M14 8.25H16V5H13.5C10.86 5 9.25 6.58 9.25 9.34V11H7V14.5H9.25V21H13V14.5H15.55L16 11H13V9.65C13 8.72 13.32 8.25 14 8.25Z" />
            </svg>
          </a>
          <a
            className="ourWhy__socialLink"
            href="https://www.instagram.com/joinbetterintentions"
            target="_blank"
            rel="noreferrer"
            aria-label="Better Intentions on Instagram"
            title="Instagram"
          >
            <svg className="ourWhy__socialIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M7.75 2H16.25C19.42 2 22 4.58 22 7.75V16.25C22 19.42 19.42 22 16.25 22H7.75C4.58 22 2 19.42 2 16.25V7.75C2 4.58 4.58 2 7.75 2ZM7.75 4C5.68 4 4 5.68 4 7.75V16.25C4 18.32 5.68 20 7.75 20H16.25C18.32 20 20 18.32 20 16.25V7.75C20 5.68 18.32 4 16.25 4H7.75ZM12 7.25C14.62 7.25 16.75 9.38 16.75 12C16.75 14.62 14.62 16.75 12 16.75C9.38 16.75 7.25 14.62 7.25 12C7.25 9.38 9.38 7.25 12 7.25ZM12 9.25C10.48 9.25 9.25 10.48 9.25 12C9.25 13.52 10.48 14.75 12 14.75C13.52 14.75 14.75 13.52 14.75 12C14.75 10.48 13.52 9.25 12 9.25ZM17.05 6.7C17.74 6.7 18.3 7.26 18.3 7.95C18.3 8.64 17.74 9.2 17.05 9.2C16.36 9.2 15.8 8.64 15.8 7.95C15.8 7.26 16.36 6.7 17.05 6.7Z" />
            </svg>
          </a>
        </div>
      </footer>
    </main>
  );
}
